import { ClearOutlined, DownOutlined } from '@ant-design/icons';
import { ProFormField } from '@ant-design/pro-form';
import { useHover } from 'ahooks';
import { Button, Dropdown, Form, FormInstance, Input, InputNumber, Select, Space, Tooltip } from 'antd';
import classnames from 'classnames';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwapRight from '@/assets/images/icons/SwapRight.react.svg?react';
import { DATA_TID } from '@/infrastructure/cypress-ids';
import { SelectNumberFormType } from '@/presentation/components/form';
import { FieldsOperator } from '@/presentation/components/form/proto';
import { NUMBER_MAX_VALUE } from '@/presentation/components/tds-form/config';
import { TdsFormLabel } from '@/presentation/components/tds-form/tds-form-label';
import styles from './select-number.module.scss';

export enum NumberOperator {
  GR = 'GR',     // 大于
  GE = 'GE',     // 大于等于
  LS = 'LS',     // 小于
  LE = 'LE',     // 小于等于
  NOT = 'NOT',   // 不等于
  RANGE = 'RANGE',  // 范围
}

export type RawNumber = number | undefined | null;
const rangeList: string[] = [NumberOperator.RANGE, FieldsOperator.Between];
export interface NumberType {
  operator: string;
  value: RawNumber[];
}

const SelectNumberGroup: FC<{
  entry: SelectNumberFormType,
  onChange?: (value: any) => void,
  formChange?: (value: Record<string, any>, fields: Record<string, any>) => void;
  initValue?: any;
  value?: NumberType;
  form?: FormInstance,
}> = (props) => {
  const { entry, onChange, value } = props;
  const { options, dropDownMode = false } = entry;
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const defaultValue = {
    operator: options?.[0].value,
    value: props.initValue || [],
  };
  const [innerValue, setInnerValue] = useState<NumberType>(defaultValue);
  const [filterText, setFilterText] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    setInnerValue(defaultValue);

    form.setFieldsValue({
      min: defaultValue?.value?.[0],
      max: defaultValue?.value?.[1],
    });

    if (!defaultValue?.value?.[0] && !defaultValue?.value?.[1]) {
      setFilterText('');
      return;
    }

    setFilterText(`${defaultValue?.value?.[0] ?? ''}~${defaultValue?.value?.[1] ?? ''}`);
  }, [props.initValue]);

  // 表单更新赋值，处理对比模式下条件B第一次输入无效问题
  useEffect(() => {
    if (!props.initValue) {
      setInnerValue(value?.value ? value : {
        operator: options?.[0].value,
        value: [],
      });

      form.setFieldsValue({
        min: undefined,
        max: undefined,
      });
      setFilterText('');
    }
  }, [value]);

  const onSelect = (operator: string) => {
    triggerValueChange({
      operator,
      value: [],
    });
  };

  const triggerValueChange = (value: NumberType) => {
    setInnerValue(value);
    onChange?.(value);
  };

  const onInputLeft = (leftValue: number | null) => {
    if (rangeList.includes(innerValue.operator)) {
      triggerValueChange({
        ...innerValue,
        value: [leftValue, innerValue.value?.length === 2 ? innerValue.value[1] : undefined],
      });
    } else {
      triggerValueChange({
        ...innerValue,
        value: !isNil(leftValue) ? [leftValue] : [],
      });
    }
  };

  const onInputRight = (rightValue: number | null) => {
    if (rangeList.includes(innerValue.operator)) {
      triggerValueChange({
        ...innerValue,
        value: [innerValue.value?.length === 2 ? innerValue.value[0] : undefined, rightValue],
      });
    } else {
      triggerValueChange({
        ...innerValue,
        value: [rightValue],
      });
    }
  };

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);

  const onConfirm = () => {
    const values = form.getFieldsValue();
    hideOverlay();

    if (!values?.value?.[0] && !values?.value?.[1]) {
      setFilterText('');
    } else {
      setFilterText(`${values?.min ?? ''}~${values?.max ?? ''}`);
    }

    setTimeout(() => {
      triggerValueChange({
        ...innerValue,
        value: [values?.min, values?.max],
      });
      if (entry.enableAutoSearch) {
        props.formChange?.(innerValue, props.form?.getFieldsValue());
      }
    }, 1);
  };

  const overlay = (
    <div className={styles.customOverlay}>
      <div>
        <Form form={form} autoFocus>
          <Form.Item name='min' label='' noStyle>
            <InputNumber
              min={entry.min}
              max={entry.max ?? NUMBER_MAX_VALUE}
              controls={false}
              data-tid={`${DATA_TID.selectNumber.inputLeft}-${entry.name}`}
            />
          </Form.Item>
          {rangeList.includes(innerValue.operator) ? <>
            <SwapRight />
            <Form.Item name='max' label='' noStyle>
              <InputNumber
                max={NUMBER_MAX_VALUE}
                controls={false}
                data-tid={`${DATA_TID.selectNumber.inputRight}-${entry.name}`}
              />
            </Form.Item>
          </> : null}
          <Tooltip title={t('重置')}>
            <ClearOutlined style={{ marginLeft: 12 }} onClick={() => form.resetFields()} />
          </Tooltip>
        </Form>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button onClick={hideOverlay} size='small'>{t('取消')}</Button>
          <Button type={'primary'} onClick={onConfirm} size='small'>{t('确定')}</Button>
        </Space>
      </div>
    </div>
  );

  if (dropDownMode) {
    return (
      <Fragment>
        <div className={classnames(styles.selectGroup, 'select-group')}>
          <span className={styles.selectLeft}>
            <Select
              options={options}
              allowClear={false}
              value={innerValue.operator || options[0].value}
              onChange={onSelect}
              dropdownClassName={'reset-font-size'}
              data-tid={`${DATA_TID.selectNumber.leftSelect}-${entry.name}`}
              dropdownMatchSelectWidth={100}
            />
          </span>
          <span className={classnames(styles.selectRight, styles.panel)}>
            <div className={styles.selectRightLayer} style={{ position: 'relative' }}>
              <Input
                placeholder={t('选择范围')}
                onFocus={showOverlay}
                suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }} />}
                style={{ width: '100%' }}
                value={filterText}
              />
              <Dropdown
                overlay={overlay}
                trigger={['click']}
                visible={visible}
                destroyPopupOnHide={false}
                onVisibleChange={visible => setVisible(visible)}
              >
                <div style={{ height: 1 }} />
              </Dropdown>
            </div>
          </span>
        </div>
      </Fragment>
    );
  }

  return <Fragment>
    <div className={classnames(styles.selectGroup, 'select-group')}>
      <span className={styles.selectLeft}>
        <Select
          options={options}
          allowClear={false}
          value={innerValue.operator || options[0].value}
          onChange={onSelect}
          dropdownClassName={'reset-font-size'}
          data-tid={`${DATA_TID.selectNumber.leftSelect}-${entry.name}`}
          dropdownMatchSelectWidth={100}
        />
      </span>
      <span className={styles.selectRight}>
        <div className={styles.selectRightContent}>
          <InputNumber
            min={entry.min}
            max={entry.max ?? NUMBER_MAX_VALUE}
            value={innerValue.value?.[0]}
            onChange={onInputLeft}
            controls={false}
            data-tid={`${DATA_TID.selectNumber.inputLeft}-${entry.name}`}
          />
          {rangeList.includes(innerValue.operator) ? <>
            <SwapRight />
            <InputNumber
              max={NUMBER_MAX_VALUE}
              value={innerValue.value?.[1]}
              onChange={onInputRight}
              controls={false}
              data-tid={`${DATA_TID.selectNumber.inputRight}-${entry.name}`}
            />
          </> : null}
        </div>
      </span>
    </div>
  </Fragment>;
};

export const SelectNumberForm: FC<{
  entry: SelectNumberFormType,
  hidden?: boolean,
  form?: FormInstance,
  formChange?: (value: Record<string, any>, fields: Record<string, any>) => void;
}> = observer((props) => {
  const { entry, hidden, form, formChange } = props;
  const { name, convertValue, processValue, labelString, label, labelTools } = entry;
  const domRef = useRef(null);
  const isHovering = useHover(domRef);

  const initValue = !name.includes('Compare')
    ? [entry?.initialValue?.[0], entry?.initialValue?.[1]]
    : [entry?.initialValue?.[2], entry?.initialValue?.[3]];

  return <div ref={domRef} className={styles.tdsLabel}>
    <ProFormField
      name={name}
      label={<TdsFormLabel isHovering={isHovering}
        label={labelString || label}
        tools={labelTools} />}
      hidden={hidden}
      convertValue={convertValue}
      transform={(value: any, _namePath: string, allValues: any) => processValue?.(value, allValues) || entry.transform}
    >
      <SelectNumberGroup
        entry={entry}
        initValue={entry.initialValue?.length ? initValue : undefined}
        form={form}
        formChange={formChange}
      />
    </ProFormField></div>;
});
